import * as React from "react";
import {useState} from "react";
import {connect} from "react-redux";
import {
    capitalMarketAssumptionsSelector, hasAonTrustCompany, insightsEnabled, marketBackgroundsSelector,
    mediumTermViewsSelector,
    quarterlyInvestmentOutlookSelector,
} from "../../../mainReducerMapSelectors";
import {ICapitalMarketAssumption} from "../../model/thought-leadership/CapitalMarketAssumption";
import {IMediumTermView} from "../../model/thought-leadership/MediumTermView.model";
import {IMarketOutlookDocument} from "../../model/thought-leadership/MarketOutlookDocument.model";
import {byDate, formatDateAsQuarterAndYear, getQuarterAndYearByDate, QuarterFilterItems} from "../../utils/dateUtil";
import {
    download,
    getDocumentType,
    MarketOutlook,
    documentTypeEnumValueFromKey,
    ALL_YEARS, ALL_TYPES
} from "../../utils/marketOutlookUtils";
import {getSuccessData} from "../common/commonStates";
import {
    capitalMarketAssumptionsRequestPair, marketBackgroundsRequestPair,
    mediumTermViewsRequestPair,
    quarterlyInvestmentOutlookRequestPair,
} from "../common/RequesterPairs";
import {MarketOutlooksFilter} from "./MarketOutlooksFilter.component";
import {getYears} from "./MarketOutlookUtils";
import {FormattedMessage} from "react-intl";
import {multipleApiRequesterWrapper} from "../common/MultipleApiRequesterWrapper";
import {IApplicationRootState} from "../../../applicationState";
import {ErrorComponent} from "../base/Error.component";
import {getQueryStringParam} from "../../utils/browserUtil";
import {
    MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_DESC,
    MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_TITLE,
    MARKET_OUTLOOK_INTRO, MARKET_OUTLOOK_MARKET_BACKGROUND_DESC, MARKET_OUTLOOK_MARKET_BACKGROUND_TITLE,
    MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_DESC,
    MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_TITLE,
    MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_DESC, MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_TITLE
} from "../../utils/commonConstants";

export interface IMarketOutlookPagePropsFromStore {
    capitalMarketAssumptionsState: ICapitalMarketAssumption[];
    mediumTermViewsState: IMediumTermView[];
    quarterlyInvestmentOutlookState: IMarketOutlookDocument[];
    marketBackgroundsState: IMarketOutlookDocument[];
    insightsEnabled: boolean;
    aonTrustCompany: boolean;
}

export interface IMarketOutlookFilterSelections {
    yearSelection: string;
    quarterSelection: string;
    typeSelection: string;
}

export enum MarketOutlookFilterByOptions {
    BY_YEAR = "by_year",
    BY_QUARTER = "by_quarter",
    BY_TYPE = "by_type",
}


const marketOutlooksIntro = <div className="new-market-outlooks__intro-section">
    <p className="new-market-outlooks__intro-text" data-testid="new-market-outlooks__intro-text">
        {MARKET_OUTLOOK_INTRO}
    </p>
    <ul className="new-market-outlooks__doc-type-list" data-testid="new-market-outlooks__doc-type-list">
        <li className="new-market-outlooks__doc-type-item new-market-outlooks__doc-type-item--mtv">
            <span className="new-market-outlooks__doc-type-title">{MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_TITLE}</span> —{" "}
            <span className="new-market-outlooks__doc-type-desc">{MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_DESC}</span>
        </li>
        <li className="new-market-outlooks__doc-type-item new-market-outlooks__doc-type-item--cma">
            <span className="new-market-outlooks__doc-type-title">{MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_TITLE}</span> —{" "}
            <span className="new-market-outlooks__doc-type-desc">{MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_DESC}</span>
        </li>
        <li className="new-market-outlooks__doc-type-item new-market-outlooks__doc-type-item--qio">
            <span className="new-market-outlooks__doc-type-title">{MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_TITLE}</span> —{" "}
            <span className="new-market-outlooks__doc-type-desc">{MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_DESC}</span>
        </li>
        <li className="new-market-outlooks__doc-type-item new-market-outlooks__doc-type-item--mb">
            <span className="new-market-outlooks__doc-type-title">{MARKET_OUTLOOK_MARKET_BACKGROUND_TITLE}</span> —{" "}
            <span className="new-market-outlooks__doc-type-desc">{MARKET_OUTLOOK_MARKET_BACKGROUND_DESC}</span>
        </li>
    </ul>
</div>;

export const MarketOutlookPage: React.FunctionComponent<IMarketOutlookPagePropsFromStore> = (props) => {
    const documentType = getQueryStringParam("document-type");
    const preSelectedType = documentType ? documentTypeEnumValueFromKey(documentType) : ALL_TYPES;

    const allDocuments: MarketOutlook[] = [
        ...props.quarterlyInvestmentOutlookState,
        ...props.mediumTermViewsState,
        ...props.capitalMarketAssumptionsState,
        ...props.marketBackgroundsState,
    ];

    const [selectedYear, setSelectedYear] = useState<string>(ALL_YEARS);
    const [selectedType, setSelectedType] = useState<string>(preSelectedType);
    const [selectedQuarter, setSelectedQuarter] = useState<QuarterFilterItems>(QuarterFilterItems.ALL);

    const selections: IMarketOutlookFilterSelections = {
        yearSelection: selectedYear,
        quarterSelection: selectedQuarter,
        typeSelection: selectedType,
    };

    function handleFilterChange(event: any, id: string) {
        switch (id as MarketOutlookFilterByOptions) {
            case MarketOutlookFilterByOptions.BY_YEAR:
                setSelectedYear(event.target.value);
                return;
            case MarketOutlookFilterByOptions.BY_QUARTER:
                setSelectedQuarter(event.target.value);
                return;
            case MarketOutlookFilterByOptions.BY_TYPE:
                setSelectedType(event.target.value);
                return;
        }
    }

    function handleClearFilters() {
        setSelectedType(ALL_TYPES);
        setSelectedYear(ALL_YEARS);
        setSelectedQuarter(QuarterFilterItems.ALL);
    }

    function filterDocuments(document: MarketOutlook) {
        return (selectedYear === ALL_YEARS || document.date.getFullYear() === parseInt(selectedYear, 10))
            && (selectedQuarter === QuarterFilterItems.ALL
                || getQuarterAndYearByDate(document.date).quarter.toString() === selectedQuarter.toString())
            && (selectedType === ALL_TYPES || getDocumentType(document) === selectedType);
    }

    function renderDocumentList() {
        return <div className="market-outlooks-page__document-list">
            {renderDocumentsByYear(allDocuments.filter(filterDocuments))}
        </div>;
    }

    function renderDocumentsByYear(documents: MarketOutlook[]) {
        const years = getYears(documents);
        return <div>
            {years.map((year) => renderYearAndDocuments(year, documents))}
        </div>;
    }

    function renderYearAndDocuments(year: string, documents: MarketOutlook[]) {
        return <div className="market-outlooks__section-content" key={year}>
            <div className="market-outlooks__left">
                <span className="date-splash-big market-outlooks__year"
                      data-testid="market-outlooks__year">{year}</span>
            </div>
            <div className="">
                {renderDocumentsForYear(year, documents)}
            </div>
        </div>;
    }

    function renderDocumentsForYear(year: string, documents: MarketOutlook[]) {
        const docsForYearAndQuarter = documents.filter((document) => {
            return document.date.getFullYear() === Number.parseInt(year, 10);
        })
            .sort(byDate);

        return <div className="no-bullets">
            <ul className="document-list" >{docsForYearAndQuarter.map(renderDocument)}</ul>
        </div>;
    }

    function renderDocument(document: MarketOutlook,
                            index: number,
                            documents: MarketOutlook[]) {
        return <li key={index}>
            <div className="document-list__document market-outlooks-document-list__document">
                <div key={document.title}
                     className="document-list__document__info__title clickable-document-title
                                                document-title-16"
                     data-testid="document-list__document__info__title"
                     onClick={() => download(document)}>
                    {document.title}
                </div>
                <div className="document-list__document-date date-12" data-testid="document-list__document-date">
                    {formatDateAsQuarterAndYear(document.date)}
                </div>
            </div>
            {index === documents.length - 1 ? "" : renderSeparator()}
        </li>;
    }

    function renderSeparator() {
        return <div className="document-list__document-separator">
            <div className="spacer-dash--small"/>
        </div>;
    }

    const render = () => {
        if (!props.insightsEnabled || props.aonTrustCompany) {
            return <ErrorComponent/>;
        }

        return <div className="new-common-styles page-container">
            <h1 className="new-market-outlooks__header">
                <FormattedMessage id="market-outlooks.title" defaultMessage="Market Outlooks" />
            </h1>
            {marketOutlooksIntro}
            <div className="new-market-outlooks__filters">
                <MarketOutlooksFilter documents={allDocuments}
                                      handleFilterChange={handleFilterChange}
                                      handleClearFilters={handleClearFilters}
                                      selections={selections}/>
            </div>
            {renderDocumentList()}
        </div>;
    };

    return render();
};

export const mapStateToProps = (state: IApplicationRootState): IMarketOutlookPagePropsFromStore => {
    return {
        capitalMarketAssumptionsState: getSuccessData(capitalMarketAssumptionsSelector(state))!.valueSeq().toArray(),
        mediumTermViewsState: getSuccessData(mediumTermViewsSelector(state))!,
        quarterlyInvestmentOutlookState: getSuccessData(quarterlyInvestmentOutlookSelector(state))!,
        marketBackgroundsState: getSuccessData(marketBackgroundsSelector(state))!,
        insightsEnabled: insightsEnabled(state),
        aonTrustCompany: hasAonTrustCompany(state),
    };
};

const connectedMarketOutlookPage = connect(mapStateToProps)(MarketOutlookPage);

export default multipleApiRequesterWrapper(
    connectedMarketOutlookPage,
    [
        capitalMarketAssumptionsRequestPair,
        mediumTermViewsRequestPair,
        quarterlyInvestmentOutlookRequestPair,
        marketBackgroundsRequestPair,
    ],
);
